import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';

import SeoManager from './lib/SeoManager';
import ScrollDepthTracker from '@/components/marketing/ScrollDepthTracker';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import Home from './pages/Home';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Route-level code splitting: each page (and its heavy deps, e.g. charts,
// rich-text editor, pdf libs) is loaded on demand so the initial bundle only
// ships what the landing page needs. Keep the landing page (Home) eager.
const Products = lazy(() => import('./pages/Products'));
const Pricing = lazy(() => import('./pages/Pricing'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Training = lazy(() => import('./pages/Training'));
const DomiciliaryCare = lazy(() => import('./pages/DomiciliaryCare'));
const CareHomeManagement = lazy(() => import('./pages/CareHomeManagement'));
const NursingHomeManagement = lazy(() => import('./pages/NursingHomeManagement'));
const EnterpriseSolutions = lazy(() => import('./pages/EnterpriseSolutions'));
const AIFeatures = lazy(() => import('./pages/AIFeatures'));
const DataMigration = lazy(() => import('./pages/DataMigration'));
const CustomerSupport = lazy(() => import('./pages/CustomerSupport'));
const PartnerProgram = lazy(() => import('./pages/PartnerProgram'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const TrainingPortal = lazy(() => import('./pages/TrainingPortal'));
const CourseViewer = lazy(() => import('./pages/CourseViewer'));
const ManageTraining = lazy(() => import('./pages/ManageTraining'));
const TrainingConfiguration = lazy(() => import('./pages/TrainingConfiguration'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const GDPR = lazy(() => import('./pages/GDPR'));
const DataProcessing = lazy(() => import('./pages/DataProcessing'));
const PageNotFound = lazy(() => import('./lib/PageNotFound'));
const Careers = lazy(() => import('./pages/Careers'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const Compare = lazy(() => import('./pages/Compare'));
const AdminSubmissions = lazy(() => import('./pages/AdminSubmissions'));
const Profile = lazy(() => import('./pages/Profile'));
// Add lazy page imports here
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplates'));
const QuoteBuilder = lazy(() => import('./pages/QuoteBuilder'));

const PageLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
);

const AuthenticatedApp = () => {
  const { authError } = useAuth();

  // Deliberately does NOT block rendering on the auth check.
  //
  // Under Base44 the whole app waited for a public-settings fetch before painting.
  // With Cognito the session resolves locally and this site is public, so gating the
  // marketing pages behind a spinner would delay first paint for every anonymous
  // visitor to no benefit. Only the training portal needs a resolved session, and
  // TrainingAuthGate handles its own loading state.
  if (authError?.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/training" element={<Training />} />
        <Route path="/domiciliary-care" element={<DomiciliaryCare />} />
        <Route path="/care-home-management" element={<CareHomeManagement />} />
        <Route path="/nursing-home-management" element={<NursingHomeManagement />} />
        <Route path="/enterprise-solutions" element={<EnterpriseSolutions />} />
        <Route path="/ai-features" element={<AIFeatures />} />
        <Route path="/data-migration" element={<DataMigration />} />
        <Route path="/customer-support" element={<CustomerSupport />} />
        <Route path="/partner-program" element={<PartnerProgram />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/training-portal" element={<TrainingPortal />} />
        <Route path="/training/:courseId" element={<CourseViewer />} />
        <Route path="/manage-training" element={<ManageTraining />} />
        <Route path="/training-configuration" element={<TrainingConfiguration />} />
        {/* Admin-only; AdminGate enforces the Cognito group, the API enforces it for real. */}
        <Route path="/admin/submissions" element={<AdminSubmissions />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/gdpr" element={<GDPR />} />
        <Route path="/data-processing" element={<DataProcessing />} />
        <Route path="/email-templates" element={<EmailTemplatesPage />} />
        <Route path="/quote-builder" element={<QuoteBuilder />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <SeoManager />
          <ScrollDepthTracker />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App